import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useSearchParams, Link, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  LogOut, Plus, Zap, Fuel, Flame, Trash2, Droplets, Download, Share2, Check, X, AlertCircle, Upload, Settings,
  Building2, FileText, Paperclip, Eye, ChevronDown, ChevronRight, Crown, Lock, Loader2, Award, HelpCircle,
  Camera, ScanLine, BarChart3, History, Clock, Shield, Scale, ClipboardCheck, Bell, Menu, Globe, User, Users,
  Thermometer, Lightbulb, Mail, Leaf, Trophy, Sparkles, Activity, MoreVertical, RefreshCw,
  Calendar as CalendarIcon, CalendarRange
} from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import HelpTooltip, { helpContent } from '@/components/HelpTooltip';
import ReceiptScanner from '@/components/ReceiptScanner';
import CsvImporter from '@/components/CsvImporter';
import AICoPilot from '@/components/AICoPilot';
import ReductionTargetCard from '@/components/ReductionTargetCard';

// Dashboard Sub-components
import DashboardSummary from '@/components/dashboard/DashboardSummary';
import DashboardCharts from '@/components/dashboard/DashboardCharts';
import EmissionsTable from '@/components/dashboard/EmissionsTable';
import DataEntryForm from '@/components/dashboard/DataEntryForm';
import EvidenceModal from '@/components/dashboard/EvidenceModal';

// Custom Hooks
import { useReports } from '@/hooks/useReports';
import { useCamera } from '@/hooks/useCamera';
import { useEvidence } from '@/hooks/useEvidence';
import { useEmissionsData } from '@/hooks/useEmissionsData';
import { useVisibleCategories } from '@/hooks/useVisibleCategories';

import { getErrorMessage } from '@/utils/errorHelpers';
import { usePermissions, FEATURES } from '@/permissions/usePermissions';
import { getEntryLimit, formatLimit } from '@/permissions/tierLimits';
import {
  formatActivityDate,
  formatEntryDate,
  getReportingPeriods,
  getFYLabel,
  getFinancialYear,
  calculateFYAllocation
} from '@/utils/financialYearUtils';
import { calculateEmissions, calculateMultiYearEmissions } from '@/utils/emissionCalculator';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const DashboardPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get context from MemberLayout (provides selectedYear, currentOrg, subscriptionStatus)
  const outletContext = useOutletContext() || {};

  // Use org from MemberLayout context (it successfully fetches the org as shown in header)
  const currentOrg = outletContext?.currentOrg;
  const subscriptionStatus = outletContext?.subscriptionStatus;

  // Use selected period from MemberLayout context for filtering
  const contextSelectedYear = outletContext?.selectedYear;
  const contextCustomDateRange = outletContext?.customDateRange;

  const {
    emissions,
    loading,
    deletingEntry,
    historyLoading,
    entryHistory,
    fetchEmissionsWithDocs,
    handleDeleteEntry,
    fetchEntryHistory,
    addEmissionEntry,
    updateEmissionEntry
  } = useEmissionsData(currentOrg, user);

  const {
    groupedCategories = {},
    flatCategories: categories = [],
    loading: categoriesLoading
  } = useVisibleCategories(currentOrg);

  const {
    evidenceUploading,
    deletingEvidence,
    uploadCapturedImage,
    handleEvidenceUpload,
    handleDeleteEvidence
  } = useEvidence(currentOrg, user, fetchEmissionsWithDocs);

  const [showForm, setShowForm] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [showEvidenceUpload, setShowEvidenceUpload] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [showReceiptScanner, setShowReceiptScanner] = useState(false);
  const [receiptScanCategory, setReceiptScanCategory] = useState('fuel');

  // Derive periodType and selectedYear from context (for filtering) with fallbacks
  const periodType = contextSelectedYear === 'all' ? 'all_time' :
    contextSelectedYear === 'custom' ? 'custom' : 'financial_year';
  const selectedYear = typeof contextSelectedYear === 'number' ? contextSelectedYear :
    (new Date().getMonth() >= 6 ? new Date().getFullYear() + 1 : new Date().getFullYear());
  const customDateRange = contextCustomDateRange || {
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  };

  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ start: null, end: null });
  const [organisations, setOrganisations] = useState([]);
  // Keep setCurrentOrg and setSubscriptionStatus as no-ops for backward compatibility with existing code
  const setCurrentOrg = () => { };
  const setSubscriptionStatus = () => { };
  // Mock fetchOrganisations to prevent crashes since orgs are now handled by MemberLayout context
  const fetchOrganisations = () => { };
  const [subscriptionOverview, setSubscriptionOverview] = useState(null);
  const [grantCompliance, setGrantCompliance] = useState(null);
  const [newOrgData, setNewOrgData] = useState({ name: '', abn: '', state: '', sector: '' });
  const [pendingSessionId, setPendingSessionId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const evidenceInputRef = useRef(null);
  const formReceiptInputRef = useRef(null);

  // Camera capture state for evidence modal
  const {
    cameraMode, setCameraMode,
    cameraStream, setCameraStream,
    capturedImage, setCapturedImage,
    videoRef, canvasRef,
    startCamera: startEvidenceCamera,
    stopCamera: stopEvidenceCamera,
    captureImage: captureEvidenceImage
  } = useCamera();

  const closeEvidenceModal = () => {
    setShowEvidenceUpload(null);
    stopEvidenceCamera();
    setCapturedImage(null);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  // Camera capture state for emission form
  const {
    cameraMode: formCameraMode, setCameraMode: setFormCameraMode,
    cameraStream: formCameraStream, setCameraStream: setFormCameraStream,
    capturedImage: formCapturedImage, setCapturedImage: setFormCapturedImage,
    videoRef: formVideoRef, canvasRef: formCanvasRef,
    startCamera: startFormCamera,
    stopCamera: stopFormCamera,
    captureImage: captureFormImage
  } = useCamera();

  // Flip card state for Carbon Footprint Summary cards
  const [flippedCards, setFlippedCards] = useState({
    gross: false,
    offsets: false,
    net: false
  });

  // Reduction Target state
  const [reductionTarget, setReductionTarget] = useState(null);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showReportConsentModal, setShowReportConsentModal] = useState(false);
  const [reportConsentAccepted, setReportConsentAccepted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showEvidenceDeleteConfirm, setShowEvidenceDeleteConfirm] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [showComplianceMenu, setShowComplianceMenu] = useState(false);
  const [showCsvImporter, setShowCsvImporter] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showOpportunitiesMenu, setShowOpportunitiesMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null); // For kebab menu in Recent Data Entries

  // Demo entry handling on upgrade
  const [showDemoEntriesModal, setShowDemoEntriesModal] = useState(false);
  const [demoEntriesCount, setDemoEntriesCount] = useState(0);
  const [demoEntriesOrgId, setDemoEntriesOrgId] = useState(null);
  const [handlingDemoEntries, setHandlingDemoEntries] = useState(false);

  // Role-based permissions
  const [userRole, setUserRole] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [platformLogo, setPlatformLogo] = useState('/placeholder-logo.svg');

  // Notification state
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Carbon summary and activity state
  const [carbonSummary, setCarbonSummary] = useState({
    gross_emissions: 0,
    carbon_offsets: 0,
    net_emissions: 0,
    offsets_available: false
  });
  const [recentActivity, setRecentActivity] = useState([]);

  // Pagination and entry counts for EmissionsTable
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [allOffsetAllocations, setAllOffsetAllocations] = useState([]);
  const [hasOffsetsFeature, setHasOffsetsFeature] = useState(false);

  // Tips & Goals Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  // PDF Reports hook - async PDF generation
  const {
    requestReport,
    hasPendingReport,
    pendingJob: pendingReportJob
  } = useReports(currentOrg?.id, currentOrg?.name);

  const [targetFormData, setTargetFormData] = useState({
    baseline_year: new Date().getFullYear(),
    reduction_target_percent: 20,
    target_year: 2030
  });

  const [formData, setFormData] = useState({
    category: '',
    quantity: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_date_range: false,
    entry_date: new Date().toISOString().split('T')[0], // Keep for backward compat
    receipt_file: null,
    notes: ''
  });
  const [editingEntry, setEditingEntry] = useState(null); // Track which entry is being edited
  const [showEditModal, setShowEditModal] = useState(null); // For edit popup modal
  const [editModalData, setEditModalData] = useState({
    category: '',
    quantity: '',
    entry_date: '',
    notes: ''
  });



  const [profileData, setProfileData] = useState({
    company_name: '',
    abn: '',
    state: '',
    electricity_grid_region: '',
    asrs_reporting_group: '',
    sector: '',
    employee_count: '',
    reporting_year: '',
    revenue: '',
    website: ''
  });

  // My Account state
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountTab, setAccountTab] = useState('profile'); // 'profile' or 'password'

  // Categories that are typically billed over a period (utility bills)
  const periodBasedCategories = ['electricity', 'electricity_greenpower', 'natural_gas'];

  // Handle category change - auto-toggle to date range for utility categories
  const handleCategoryChange = (category) => {
    const shouldBeRange = periodBasedCategories.includes(category);
    setFormData(prev => ({
      ...prev,
      category,
      is_date_range: shouldBeRange,
      end_date: shouldBeRange ? prev.end_date || prev.start_date : ''
    }));
  };

  const australianStates = [
    { code: 'NSW', name: 'New South Wales', factor: 0.64 },
    { code: 'VIC', name: 'Victoria', factor: 0.82 },
    { code: 'QLD', name: 'Queensland', factor: 0.67 },
    { code: 'WA', name: 'Western Australia', factor: 0.51 },
    { code: 'SA', name: 'South Australia', factor: 0.25 },
    { code: 'TAS', name: 'Tasmania', factor: 0.15 },
    { code: 'ACT', name: 'Australian Capital Territory', factor: 0.64 },
    { code: 'NT', name: 'Northern Territory', factor: 0.54 }
  ];

  // Grid regions for WA and NT (different electricity networks)
  const gridRegions = {
    'WA': [
      { code: 'WA_SWIS', name: 'South West Interconnected System (Perth, SW WA)', factor: 0.50 },
      { code: 'WA_NWIS', name: 'North West Interconnected System (Pilbara)', factor: 0.56 }
    ],
    'NT': [
      { code: 'NT_DKIS', name: 'Darwin Katherine Interconnected System', factor: 0.56 },
      { code: 'NT_AIUS', name: 'Alice Springs / Tennant Creek (isolated)', factor: 0.56 }
    ]
  };

  // ASRS Reporting Groups - determines disclosure timeline and assurance requirements
  const asrsGroups = [
    {
      value: 1,
      name: 'Group 1 - Large Entities',
      description: 'First report: FY 2024-25. Reasonable assurance from FY 2029-30.',
      criteria: 'Large listed entities meeting 2 of: >$500M revenue, >$1B assets, >500 employees'
    },
    {
      value: 2,
      name: 'Group 2 - Mid-tier Entities',
      description: 'First report: FY 2026-27. Reasonable assurance from FY 2031-32.',
      criteria: 'Listed entities meeting 2 of: >$200M revenue, >$500M assets, >250 employees'
    },
    {
      value: 3,
      name: 'Group 3 - Smaller Entities',
      description: 'First report: FY 2027-28. Reasonable assurance from FY 2032-33.',
      criteria: 'Other large proprietary companies and registered schemes'
    }
  ];

  const sectorOptions = [
    'Agriculture, Forestry and Fishing',
    'Mining',
    'Manufacturing',
    'Electricity, Gas, Water and Waste Services',
    'Construction',
    'Wholesale Trade',
    'Retail Trade',
    'Accommodation and Food Services',
    'Transport, Postal and Warehousing',
    'Information Media and Telecommunications',
    'Financial and Insurance Services',
    'Rental, Hiring and Real Estate Services',
    'Professional, Scientific and Technical Services',
    'Administrative and Support Services',
    'Public Administration and Safety',
    'Education and Training',
    'Health Care and Social Assistance',
    'Arts and Recreation Services',
    'Other Services'
  ];

  // Initialize: set up click handlers and handle payment return
  useEffect(() => {
    // Setup click outside handlers
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="strategy-menu-btn"]') && !e.target.closest('.strategy-dropdown')) {
        setShowComplianceMenu(false);
      }
      if (!e.target.closest('[data-testid="opportunities-menu-btn"]') && !e.target.closest('.opportunities-dropdown')) {
        setShowOpportunitiesMenu(false);
      }
      if (!e.target.closest('[data-testid="help-menu-btn"]') && !e.target.closest('.help-dropdown')) {
        setShowHelpMenu(false);
      }
      if (!e.target.closest('[data-testid="mobile-menu-btn"]') && !e.target.closest('.mobile-menu-dropdown')) {
        setShowMobileMenu(false);
      }
      if (!e.target.closest('[data-testid="settings-menu-btn"]') && !e.target.closest('.settings-dropdown')) {
        setShowSettingsMenu(false);
      }
      if (!e.target.closest('[data-testid="export-menu-btn"]') && !e.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
      if (!e.target.closest('[data-testid="notifications-btn"]') && !e.target.closest('.notifications-dropdown')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside);

    // Handle payment return
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      handlePaymentReturn(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, '', '/dashboard');
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Fetch user role when organisation changes
  useEffect(() => {
    if (currentOrg?.id) {
      fetchUserRole();
    }
  }, [currentOrg?.id]);

  // Poll payment status - using direct Supabase query
  const handlePaymentReturn = async (sessionId) => {
    setPaymentProcessing(true);
    let attempts = 0;
    const maxAttempts = 5;

    const pollStatus = async () => {
      try {
        // Query payment_transactions table directly
        const { data: transaction, error } = await supabase
          .from('payment_transactions')
          .select('payment_status, organisation_id')
          .eq('session_id', sessionId)
          .single();

        if (error) throw error;

        if (transaction?.payment_status === 'paid') {
          toast.success('Payment successful! Your subscription has been upgraded.');
          fetchOrganisations();
          fetchSubscriptionOverview();
          setPaymentProcessing(false);
          // Clean URL
          window.history.replaceState({}, '', '/dashboard');

          // Check if this was a new org payment
          if (transaction.organisation_id === 'pending_new') {
            setPendingSessionId(sessionId);
            setShowAddOrgModal(true);
          } else if (transaction.organisation_id) {
            // Check for demo entries that need handling (upgrade from Demo tier)
            checkForDemoEntries(transaction.organisation_id);
          }
          return;
        }

        if (transaction?.payment_status === 'expired') {
          toast.error('Payment session expired');
          setPaymentProcessing(false);
          window.history.replaceState({}, '', '/dashboard');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          toast.info('Payment is being processed. Please refresh in a moment.');
          setPaymentProcessing(false);
          window.history.replaceState({}, '', '/dashboard');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setPaymentProcessing(false);
        window.history.replaceState({}, '', '/dashboard');
      }
    };

    pollStatus();
  };

  const fetchSubscriptionOverview = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      // Get user's organisations with subscription info
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) return;

      // Get all organisations where user is owner or member
      const { data: ownedOrgs } = await supabase
        .from('organisations')
        .select('id, name, subscription_tier, subscription_status, stripe_subscription_id')
        .eq('owner_id', userData.id);

      const { data: memberOrgs } = await supabase
        .from('organisation_members')
        .select('organisation:organisations(id, name, subscription_tier, subscription_status, stripe_subscription_id)')
        .eq('user_id', userData.id)
        .eq('invitation_status', 'accepted');

      const allOrgs = [
        ...(ownedOrgs || []),
        ...(memberOrgs?.map(m => m.organisation).filter(Boolean) || [])
      ];

      // Calculate overview
      const activeSubs = allOrgs.filter(o => o.subscription_status === 'active' && o.subscription_tier !== 'demo');
      setSubscriptionOverview({
        total_organisations: allOrgs.length,
        active_subscriptions: activeSubs.length,
        organisations: allOrgs.map(o => ({
          id: o.id,
          name: o.name,
          tier: o.subscription_tier,
          status: o.subscription_status
        }))
      });
    } catch (error) {
      console.error('Error fetching subscription overview:', error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        setIsAdmin(false);
        return;
      }

      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('supabase_user_id', authUser.id)
        .single();

      setIsAdmin(userData?.role === 'superadmin');
    } catch (error) {
      // Not an admin, that's fine
      setIsAdmin(false);
    }
  };

  // Initiate Stripe checkout - using Edge Function
  const initiateUpgrade = async (packageId, orgId = null) => {
    try {
      setPaymentProcessing(true);

      const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          organisation_id: orgId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.message || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  // Create paid organisation after successful payment
  const createPaidOrganisation = async () => {
    if (!pendingSessionId || !newOrgData.name) {
      toast.error('Please fill in organisation name');
      return;
    }

    try {
      // Create paid organisation via direct Supabase
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) throw new Error('User not found');

      // Create the organisation
      const { data: newOrg, error: orgError } = await supabase
        .from('organisations')
        .insert({
          owner_id: userData.id,
          name: newOrgData.name,
          abn: newOrgData.abn || null,
          state: newOrgData.state || null,
          sector: newOrgData.sector || null,
          subscription_status: 'pro',
          subscription_tier: 'pro',
          is_primary: false
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Update payment transaction with new org ID if pending
      if (pendingSessionId) {
        await supabase
          .from('payment_transactions')
          .update({ organisation_id: newOrg.id })
          .eq('session_id', pendingSessionId)
          .eq('organisation_id', 'pending_new');
      }

      // Add audit log
      await supabase.from('audit_logs').insert({
        organisation_id: newOrg.id,
        resource_type: 'organisation',
        resource_id: newOrg.id,
        action: 'create',
        description: `Created organisation: ${newOrgData.name}`
      });

      toast.success('Organisation created successfully!');
      setShowAddOrgModal(false);
      setNewOrgData({ name: '', abn: '', state: '', sector: '' });
      setPendingSessionId(null);
      fetchOrganisations();
      setCurrentOrg(newOrg);
    } catch (error) {
      console.error('Error creating organisation:', error);
      toast.error(getErrorMessage(error, 'Failed to create organisation'));
    }
  };

  // Download PDF Report - Async flow via Edge Function + Render service
  // Works for both Pro (full report) and non-Pro users (sample report)
  const downloadPDFReport = async () => {
    const isPro = subscriptionStatus?.tier === 'pro';

    setPdfDownloading(true);
    try {
      // Use async Edge Function flow for all users
      // Pass is_sample=true for non-Pro users to get watermarked sample
      const result = await requestReport(
        selectedYear,
        'emissions_pdf',
        user?.user_metadata?.full_name,
        !isPro  // is_sample = true for non-Pro users
      );

      if (result.success) {
        toast.success(
          <div>
            <p className="font-medium">
              {isPro ? 'Report generation started!' : 'Sample report generation started!'}
            </p>
            <p className="text-sm text-slate-500">
              We'll email you when it's ready. View progress on the{' '}
              <Link to="/reports" className="text-[#003B5C] underline">My Reports</Link> page.
              {!isPro && ' Upgrade to Pro for the full audit-ready version.'}
            </p>
          </div>,
          { duration: 6000 }
        );
      } else {
        if (result.error?.includes('already being generated')) {
          toast.info('A report is already being generated. Check the My Reports page for status.');
        } else {
          toast.error(result.error || 'Failed to start report generation');
        }
      }
    } catch (error) {
      console.error('Error requesting PDF:', error);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setPdfDownloading(false);
    }
  };

  // Toggle public profile - using direct Supabase
  const togglePublicProfile = async () => {
    try {
      const newState = !currentOrg?.public_profile_enabled;

      const { error } = await supabase
        .from('organisations')
        .update({
          public_profile_enabled: newState,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      // Update local state
      setCurrentOrg(prev => ({
        ...prev,
        public_profile_enabled: newState
      }));

      toast.success(newState ? 'Public profile enabled!' : 'Public profile disabled');
    } catch (error) {
      console.error('Error toggling public profile:', error);
      toast.error('Failed to update public profile');
    }
  };

  // Toggle showing intensity metric on public profile - using direct Supabase
  const toggleShowIntensity = async () => {
    try {
      const newState = !(currentOrg?.show_intensity_on_profile ?? true);

      const { error } = await supabase
        .from('organisations')
        .update({
          show_intensity_on_profile: newState,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      // Update local state
      setCurrentOrg(prev => ({
        ...prev,
        show_intensity_on_profile: newState
      }));

      toast.success(newState ? 'Intensity metric shown on public profile' : 'Intensity metric hidden from public profile');
    } catch (error) {
      console.error('Error toggling intensity display:', error);
      toast.error('Failed to update intensity setting');
    }
  };

  // Refetch when organisation changes (from MemberLayout context)
  useEffect(() => {
    if (currentOrg?.id) {
      fetchEmissionsWithDocs();
      fetchSubscriptionStatus();
      fetchReductionTarget();
      fetchCarbonSummary();
      fetchRecentActivity();
      fetchProfile();
      fetchSubscriptionOverview();
      checkAdminStatus();
      fetchNotifications();
      generateNotifications();
      fetchPendingInvitations();
      fetchPlatformLogo();
    } else {
      // If no org after a short delay, show empty state
      const timer = setTimeout(() => {
        // loading state is handled by useEmissionsData hook now relative to MemberLayout
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentOrg?.id]);

  // Refetch carbon summary and recent activity when selected year or period type changes
  useEffect(() => {
    if (currentOrg) {
      fetchCarbonSummary();
      fetchRecentActivity();
    }
  }, [selectedYear, periodType, contextSelectedYear]);

  // Auto-rotate carousel every 6 seconds
  useEffect(() => {
    const carouselItems = getCarouselItems();
    if (carouselItems.length <= 1) return;

    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselItems.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [currentOrg, reductionTarget]);

  // Get carousel items based on user's context
  const getCarouselItems = () => {
    const items = [];

    // Winning a Tender tip (Pro users only)
    if (currentOrg?.subscription_status === 'pro') {
      items.push('tender');
    }

    // Reduction Target Progress (always show)
    items.push('target');

    return items;
  };

  // Auth headers helper removed - no longer using axios backend calls


  const fetchSubscriptionStatus = async () => {
    if (!currentOrg?.id) return;
    try {
      // Fetch subscription info directly from organisation
      const { data, error } = await supabase
        .from('organisations')
        .select('subscription_status, subscription_tier, entry_count, subscription_override, subscription_override_expiry')
        .eq('id', currentOrg.id)
        .single();

      if (error) throw error;

      // Determine effective subscription
      let effectiveTier = data.subscription_tier;
      if (data.subscription_override &&
        (!data.subscription_override_expiry || new Date(data.subscription_override_expiry) > new Date())) {
        effectiveTier = data.subscription_override.replace('manual_', '');
      }

      const entryLimit = getEntryLimit(effectiveTier);
      setSubscriptionStatus({
        status: data.subscription_status,
        tier: effectiveTier,
        entry_count: data.entry_count || 0,
        is_pro: effectiveTier === 'pro',
        can_add_entries: (data.entry_count || 0) < entryLimit,
        entry_limit: entryLimit
      });
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const fetchGrantCompliance = async () => {
    // Grant compliance - using direct Supabase queries
    try {
      if (!currentOrg?.id) return;

      // Check if user has emissions data for at least 3 consecutive years
      const { data: emissionsData } = await supabase
        .from('emissions_data')
        .select('financial_year')
        .eq('organisation_id', currentOrg.id);

      const years = [...new Set((emissionsData || []).map(e => e.financial_year))].sort();
      const hasThreeYears = years.length >= 3;

      // Check if baseline year is set
      const hasBaseline = !!currentOrg.reduction_baseline_year;

      // Check if reduction target exists
      const hasTarget = !!currentOrg.reduction_target_percent;

      // Get list of categories logged
      const categories = [...new Set((emissionsData || []).map(e => e.category))];

      setGrantCompliance({
        is_compliant: hasThreeYears && hasBaseline && hasTarget,
        has_three_years: hasThreeYears,
        has_baseline: hasBaseline,
        has_target: hasTarget,
        years_with_data: years,
        categories_logged: categories,
        checklist: {
          emissions_data: hasThreeYears,
          baseline_set: hasBaseline,
          target_set: hasTarget
        }
      });
    } catch (error) {
      console.error('Error fetching grant compliance:', error);
    }
  };

  // Notification functions - using direct Supabase access
  const fetchNotifications = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) return;

      // Fetch notifications - handle missing table gracefully
      const { data: notifs, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userData.id)
        .eq('is_dismissed', false)
        .order('created_at', { ascending: false })
        .limit(10);

      // Silently handle errors (table may not exist)
      if (error) {
        // Don't log 404/406 errors for missing tables
        if (!error.code?.includes('42P01') && !error.message?.includes('406')) {
          console.error('Error fetching notifications:', error);
        }
        return;
      }

      setNotifications(notifs || []);
      setNotificationCount(notifs?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      // Silently fail - notifications are not critical
    }
  };

  const generateNotifications = async () => {
    // Generate notifications via direct Supabase - skip if table doesn't exist
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData || !currentOrg?.id) return;

      // First check if notifications table exists by doing a simple query
      const { error: tableCheck } = await supabase
        .from('notifications')
        .select('id')
        .limit(1);

      // If table doesn't exist or we can't access it, skip notification generation
      if (tableCheck) return;

      // Check for missing data and create notifications
      const notificationsToCreate = [];
      const now = new Date().toISOString();

      // Check if no emissions data exists
      const { data: emissionsCount } = await supabase
        .from('emissions_data')
        .select('id', { count: 'exact', head: true })
        .eq('organisation_id', currentOrg.id);

      if (!emissionsCount || emissionsCount.length === 0) {
        notificationsToCreate.push({
          user_id: userData.id,
          title: 'Add Your First Emission Entry',
          message: 'Start tracking your carbon footprint by adding emission data.',
          type: 'info',
          link: '/dashboard',
          is_read: false,
          is_dismissed: false,
          created_at: now
        });
      }

      // Check if reduction target is not set
      if (!currentOrg.reduction_target_percent) {
        notificationsToCreate.push({
          user_id: userData.id,
          title: 'Set Your Reduction Target',
          message: 'Define your emission reduction goals to track progress.',
          type: 'info',
          link: '/dashboard',
          is_read: false,
          is_dismissed: false,
          created_at: now
        });
      }

      // Insert notifications (upsert to avoid duplicates)
      if (notificationsToCreate.length > 0) {
        for (const notif of notificationsToCreate) {
          // Check if similar notification already exists
          const { data: existing } = await supabase
            .from('notifications')
            .select('id')
            .eq('user_id', notif.user_id)
            .eq('title', notif.title)
            .eq('is_dismissed', false)
            .maybeSingle();

          if (!existing) {
            await supabase.from('notifications').insert(notif);
          }
        }
      }

      setTimeout(() => fetchNotifications(), 500);
    } catch (error) {
      // Silently fail - notifications are not critical
    }
  };

  const markNotificationRead = async (notificationId, link) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (!error) {
        setNotifications(prev => prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true } : n
        ));
        setNotificationCount(prev => Math.max(0, prev - 1));
      }

      if (link) {
        navigate(link);
        setShowNotifications(false);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) return;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userData.id)
        .eq('is_read', false);

      if (!error) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setNotificationCount(0);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      // Silently fail
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;

      const { error } = await supabase
        .from('notifications')
        .update({ is_dismissed: true })
        .eq('id', notificationId);

      if (!error) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        if (wasUnread) {
          setNotificationCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      // Silently fail
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'grant_deadline': return <Award className="w-4 h-4 text-amber-500" />;
      case 'subscription': return <Crown className="w-4 h-4 text-purple-500" />;
      case 'compliance': return <ClipboardCheck className="w-4 h-4 text-blue-500" />;
      default: return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-red-500 bg-red-50';
      case 'high': return 'border-l-4 border-amber-500 bg-amber-50';
      default: return 'border-l-4 border-slate-200';
    }
  };

  const fetchReductionTarget = async () => {
    if (!currentOrg?.id) return;
    try {
      // Use direct Supabase RPC call
      const { data, error } = await supabase.rpc('get_reduction_target_progress', {
        p_org_id: currentOrg.id
      });

      if (error) throw error;

      if (data?.has_target) {
        // Also fetch offset allocations for the current year to calculate NET emissions
        const currentYear = data.current_year || new Date().getFullYear();
        let totalOffsets = 0;

        try {
          const { data: allocations } = await supabase
            .from('offset_allocations')
            .select('amount_tco2e, carbon_offset:carbon_offsets!inner(organisation_id)')
            .eq('financial_year', currentYear)
            .eq('carbon_offset.organisation_id', currentOrg.id);

          if (allocations) {
            totalOffsets = allocations.reduce((sum, a) => sum + (a.amount_tco2e || 0), 0);
          }
        } catch (offsetError) {
          console.error('Error fetching offsets for target:', offsetError);
        }

        // Calculate NET emissions (gross - offsets) - can be negative if over-offset
        const netCurrentEmissions = (data.current_emissions || 0) - totalOffsets;

        // Recalculate progress based on net emissions
        const targetEmissions = data.baseline_emissions * (1 - data.target_percent / 100);
        let progressPercent = 0;
        if (data.baseline_emissions > 0 && data.baseline_emissions > targetEmissions) {
          progressPercent = ((data.baseline_emissions - netCurrentEmissions) / (data.baseline_emissions - targetEmissions)) * 100;
          // Cap at 100% but allow calculation to exceed for "beyond target" scenarios
          progressPercent = Math.max(0, Math.min(100, progressPercent));
        }

        setReductionTarget({
          baseline_year: data.baseline_year,
          baseline_emissions: data.baseline_emissions,
          target_year: data.target_year,
          target_emissions: targetEmissions,
          reduction_target_percent: data.target_percent,
          current_year: currentYear,
          current_emissions: netCurrentEmissions,
          gross_emissions: data.current_emissions,
          offsets_applied: totalOffsets,
          progress_percent: progressPercent,
          on_track: netCurrentEmissions <= targetEmissions
        });
      } else {
        setReductionTarget(null);
      }
    } catch (error) {
      console.error('Error fetching reduction target:', error);
    }
  };

  // Fetch carbon summary (Gross, Offsets, Net) for the selected financial year
  const fetchCarbonSummary = async () => {
    if (!currentOrg?.id) return;
    try {
      // Use direct Supabase RPC call
      const { data, error } = await supabase.rpc('get_carbon_summary', {
        p_org_id: currentOrg.id,
        p_year: selectedYear
      });

      if (error) throw error;

      setCarbonSummary({
        gross_emissions: data?.gross_emissions || 0,
        carbon_offsets: data?.carbon_offsets || 0,
        net_emissions: data?.net_emissions || 0,
        offsets_available: (data?.carbon_offsets || 0) > 0,
        is_net_zero: data?.is_net_zero || false,
        emissions_per_employee: data?.emissions_per_employee,
        emissions_per_million_revenue: data?.emissions_per_million_revenue
      });
    } catch (error) {
      console.error('Error fetching carbon summary:', error);
      setCarbonSummary({
        gross_emissions: 0,
        carbon_offsets: 0,
        net_emissions: 0,
        offsets_available: false
      });
    }
  };

  // Fetch recent activity (combined emissions + offsets) for the selected financial year
  const fetchRecentActivity = async () => {
    if (!currentOrg?.id) return;
    try {
      // Fetch carbon credit purchases for this org
      const { data: creditPurchases, error: purchaseError } = await supabase
        .from('carbon_offsets')
        .select('id, project_name, amount_tco2e, purchase_date, created_at')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (purchaseError) {
        console.error('Error fetching credit purchases:', purchaseError);
      }

      // Fetch carbon credit allocations (applied offsets) for this org and year
      // First get all offsets for this org
      const { data: orgOffsets } = await supabase
        .from('carbon_offsets')
        .select('id')
        .eq('organisation_id', currentOrg.id);

      const offsetIds = (orgOffsets || []).map(o => o.id);

      let allocations = [];
      let allAllocations = []; // All allocations regardless of year

      if (offsetIds.length > 0) {
        // Fetch allocations for the selected FY (for recentActivity display)
        const { data: allocData, error: allocError } = await supabase
          .from('offset_allocations')
          .select(`
id,
  amount_tco2e,
  financial_year,
  created_at,
  carbon_offset_id,
  carbon_offset: carbon_offsets(id, project_name)
    `)
          .eq('financial_year', selectedYear)
          .in('carbon_offset_id', offsetIds)
          .order('created_at', { ascending: false })
          .limit(10);

        if (allocError) {
          console.error('Error fetching allocations:', allocError);
        } else {
          allocations = allocData || [];
        }

        // Also fetch ALL allocations (for All-Time and Custom period calculations)
        const { data: allAllocData, error: allAllocError } = await supabase
          .from('offset_allocations')
          .select(`
id,
  amount_tco2e,
  financial_year,
  created_at,
  carbon_offset_id,
  carbon_offset: carbon_offsets(id, project_name)
          `)
          .in('carbon_offset_id', offsetIds)
          .order('created_at', { ascending: false });

        if (allAllocError) {
          console.error('Error fetching all allocations:', allAllocError);
        } else {
          allAllocations = allAllocData || [];
        }
      }

      // Store all allocations for period-based calculations
      setAllOffsetAllocations(allAllocations.map(alloc => ({
        id: alloc.id,
        amount_tco2e: alloc.amount_tco2e,
        financial_year: alloc.financial_year,
        created_at: alloc.created_at,
        project_name: alloc.carbon_offset?.project_name || 'Carbon Credit'
      })));

      // Add credit purchases as separate entries
      const purchaseActivities = (creditPurchases || []).map(credit => ({
        id: `purchase-${credit.id}`,
        type: 'offset_purchase',
        category: 'Carbon Credit Purchased',
        description: credit.project_name,
        project_name: credit.project_name,
        co2_equivalent: credit.amount_tco2e,
        amount: credit.amount_tco2e,
        entry_date: credit.purchase_date || credit.created_at,
        date: credit.purchase_date || credit.created_at,
        is_offset: true
      }));

      // Add allocation activities (applied offsets)
      const allocationActivities = allocations.map(alloc => ({
        id: `alloc-${alloc.id}`,
        type: 'offset_applied',
        category: 'Carbon Offset',
        description: alloc.carbon_offset?.project_name || 'Carbon Credit',
        project_name: alloc.carbon_offset?.project_name || 'Carbon Credit',
        co2_equivalent: alloc.amount_tco2e,
        amount: alloc.amount_tco2e,
        financial_year: alloc.financial_year,
        entry_date: alloc.created_at,
        date: alloc.created_at,
        is_offset: true
      }));

      // Combine all activities
      const allActivities = [...purchaseActivities, ...allocationActivities];
      setRecentActivity(allActivities);
      setHasOffsetsFeature(allActivities.length > 0);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivity([]);
    }
  };

  const saveReductionTarget = async () => {
    if (!currentOrg?.id) return;
    try {
      // Calculate baseline emissions from the baseline year's data
      let baselineEmissions = null;
      if (targetFormData.baseline_year) {
        const { data: emissionsData, error: emissionsError } = await supabase
          .from('emissions_data')
          .select('co2_equivalent')
          .eq('organisation_id', currentOrg.id)
          .eq('financial_year', targetFormData.baseline_year);

        if (!emissionsError && emissionsData) {
          baselineEmissions = emissionsData.reduce((sum, e) => sum + (e.co2_equivalent || 0), 0);
        }
      }

      // Update organisation directly in Supabase
      const { error } = await supabase
        .from('organisations')
        .update({
          baseline_year: targetFormData.baseline_year ? parseInt(targetFormData.baseline_year) : null,
          baseline_emissions: baselineEmissions,
          reduction_target_percent: targetFormData.reduction_target_percent ? parseFloat(targetFormData.reduction_target_percent) : null,
          target_year: targetFormData.target_year ? parseInt(targetFormData.target_year) : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      if (baselineEmissions === 0 || baselineEmissions === null) {
        toast.success('Reduction target saved! Note: No emissions data found for baseline year - add emissions to track progress.');
      } else {
        toast.success(`Reduction target saved! Baseline: ${baselineEmissions.toFixed(2)} tCO₂-e`);
      }
      setShowTargetModal(false);
      fetchReductionTarget();
    } catch (error) {
      console.error('Error saving target:', error);
      toast.error(error.message || 'Failed to save target');
    }
  };

  const switchOrganisation = (org) => {
    setCurrentOrg(org);
    setShowOrgSwitcher(false);
    setLoading(true);
    fetchEmissionsWithDocs();
    fetchProfile();
    fetchGrantCompliance();
  };

  const fetchEmissions = async () => {
    if (!currentOrg?.id) {
      setLoading(false);
      return;
    }
    try {
      // Fetch ALL emissions (not filtered by FY) - simple query without joins
      const { data: emissionsData, error } = await supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('entry_date', { ascending: false });

      if (error) throw error;

      // Transform data to match expected format
      const transformedEmissions = (emissionsData || []).map(e => ({
        ...e,
        evidence_files: []
      }));

      setEmissions(transformedEmissions);
    } catch (error) {
      console.error('Error fetching emissions:', error);
      toast.error('Failed to load emissions data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    if (!currentOrg?.id) return;
    try {
      // Use direct Supabase query
      const { data, error } = await supabase
        .from('organisations')
        .select('name, state, abn, sector, employee_count, reporting_year, revenue, website, electricity_grid_region, asrs_reporting_group')
        .eq('id', currentOrg.id)
        .single();

      if (error) throw error;

      setProfileData({
        company_name: data.name || '',
        state: data.state || '',
        electricity_grid_region: data.electricity_grid_region || '',
        asrs_reporting_group: data.asrs_reporting_group || '',
        abn: data.abn || '',
        sector: data.sector || '',
        employee_count: data.employee_count || '',
        reporting_year: data.reporting_year || '',
        revenue: data.revenue || '',
        website: data.website || ''
      });
      // Fetch grant compliance after profile loads
      fetchGrantCompliance();
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  // Load account data when My Account modal opens
  useEffect(() => {
    if (showMyAccount && user) {
      setAccountData(prev => ({
        ...prev,
        name: user.name || user.user_metadata?.full_name || '',
        email: user.email || ''
      }));
      setAccountTab('profile');
    }
  }, [showMyAccount, user]);

  // Update user profile (name)
  const handleUpdateProfile = async () => {
    if (!accountData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setAccountLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');

      // Update in Supabase Auth
      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: accountData.name.trim() }
      });

      if (authError) throw authError;

      // Also update in users table
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (userData) {
        await supabase
          .from('users')
          .update({ name: accountData.name.trim() })
          .eq('id', userData.id);
      }

      toast.success('Profile updated successfully!');
      // Refresh will happen automatically via useAuth onAuthStateChange
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setAccountLoading(false);
    }
  };

  // Update user email
  const handleUpdateEmail = async () => {
    if (!accountData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (accountData.email === user?.email) {
      toast.info('Email is the same as current');
      return;
    }

    setAccountLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: accountData.email.trim()
      });

      if (error) throw error;

      toast.success('Verification email sent! Please check your inbox to confirm the new email.');
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error.message || 'Failed to update email');
    } finally {
      setAccountLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!accountData.newPassword) {
      toast.error('New password is required');
      return;
    }

    if (accountData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Check for uppercase or special character
    const hasUppercase = /[A-Z]/.test(accountData.newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(accountData.newPassword);
    if (!hasUppercase && !hasSpecialChar) {
      toast.error('Password must contain at least 1 uppercase letter or special character');
      return;
    }

    if (accountData.newPassword !== accountData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setAccountLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: accountData.newPassword
      });

      if (error) throw error;

      toast.success('Password changed successfully!');
      setAccountData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setAccountLoading(false);
    }
  };

  // Check if org has demo entries after upgrade - using direct Supabase
  const checkForDemoEntries = async (orgId) => {
    try {
      const { count, error } = await supabase
        .from('emissions_data')
        .select('*', { count: 'exact', head: true })
        .eq('organisation_id', orgId)
        .eq('is_demo_entry', true);

      if (error) throw error;

      if (count > 0) {
        setDemoEntriesCount(count);
        setDemoEntriesOrgId(orgId);
        setShowDemoEntriesModal(true);
      }
    } catch (error) {
      console.error('Error checking demo entries:', error);
      // Don't show error to user - this is a non-critical check
    }
  };

  // Handle user's choice for demo entries - using direct Supabase
  const handleDemoEntries = async (action) => {
    setHandlingDemoEntries(true);
    try {
      let entriesAffected = 0;

      if (action === 'keep') {
        // Convert demo entries to regular entries
        const { data, error } = await supabase
          .from('emissions_data')
          .update({ is_demo_entry: false, updated_at: new Date().toISOString() })
          .eq('organisation_id', demoEntriesOrgId)
          .eq('is_demo_entry', true)
          .select();

        if (error) throw error;
        entriesAffected = data?.length || 0;
        toast.success(`${entriesAffected} demo entries have been converted to regular entries.`);
      } else {
        // Delete demo entries
        const { data, error } = await supabase
          .from('emissions_data')
          .delete()
          .eq('organisation_id', demoEntriesOrgId)
          .eq('is_demo_entry', true)
          .select();

        if (error) throw error;
        entriesAffected = data?.length || 0;
        toast.success(`${entriesAffected} demo entries have been deleted.Starting fresh!`);
      }

      setShowDemoEntriesModal(false);
      fetchEmissions(); // Refresh emissions list
    } catch (error) {
      console.error('Error handling demo entries:', error);
      toast.error('Failed to process demo entries. Please try again.');
    } finally {
      setHandlingDemoEntries(false);
    }
  };

  // Fetch user's role and permissions for current org
  const fetchUserRole = async () => {
    if (!currentOrg) return;
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id, role')
        .eq('supabase_user_id', authUser.id)
        .single();

      if (!userData) return;

      // Check if user is owner
      const isOwner = currentOrg.owner_id === userData.id;

      // Get membership role if not owner
      let memberRole = null;
      if (!isOwner) {
        const { data: membership } = await supabase
          .from('organisation_members')
          .select('role')
          .eq('organisation_id', currentOrg.id)
          .eq('user_id', userData.id)
          .single();
        memberRole = membership?.role;
      }

      const role = isOwner ? 'owner' : (memberRole || 'viewer');
      const isSuperAdmin = userData.role === 'superadmin';

      // Define permissions based on role
      const permissions = {
        view_dashboard: true,
        export_reports: role !== 'viewer',
        add_entries: role !== 'viewer',
        access_strategy: role !== 'viewer',
        access_opportunities: role !== 'viewer',
        manage_team: isOwner || isSuperAdmin,
        manage_billing: isOwner || isSuperAdmin,
        delete_organisation: isOwner || isSuperAdmin,
        transfer_ownership: isOwner || isSuperAdmin
      };

      setUserRole({ role, permissions, is_owner: isOwner });
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole({
        role: 'owner',
        permissions: {
          view_dashboard: true, export_reports: true, add_entries: true,
          access_strategy: true, access_opportunities: true, manage_team: true,
          manage_billing: true, delete_organisation: true, transfer_ownership: true
        },
        is_owner: true
      });
    }
  };

  // Fetch pending invitations for the user
  const fetchPendingInvitations = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: invitations, error } = await supabase
        .from('organisation_members')
        .select(`
invitation_token,
  invited_at,
  invitation_expires_at,
  organisations(id, name)
    `)
        .eq('invited_email', authUser.email)
        .eq('invitation_status', 'pending')
        .gt('invitation_expires_at', new Date().toISOString());

      // Handle RLS or table access errors gracefully
      if (error) {
        // Don't log 403 errors - these are expected if RLS restricts access
        if (error.code !== '42501' && !error.message?.includes('403')) {
          console.error('Error fetching invitations:', error);
        }
        return;
      }

      setPendingInvitations((invitations || []).map(inv => ({
        token: inv.invitation_token,
        organisation_name: inv.organisations?.name,
        invited_at: inv.invited_at,
        expires_at: inv.invitation_expires_at
      })));
    } catch (error) {
      // Silently fail - invitations are not critical for dashboard load
    }
  };

  // Fetch platform logo - just use default for now (platform_settings table may not exist)
  const fetchPlatformLogo = async () => {
    // Use default logo - platform_settings migration not applied yet
    setPlatformLogo('/placeholder-logo.svg');
  };

  // Accept invitation handler
  const handleAcceptInvitation = async (token) => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      // Update invitation status
      const { error } = await supabase
        .from('organisation_members')
        .update({
          invitation_status: 'accepted',
          accepted_at: new Date().toISOString(),
          user_id: userData?.id
        })
        .eq('invitation_token', token);

      if (error) throw error;

      toast.success('Invitation accepted! Refreshing...');
      fetchPendingInvitations();
      fetchOrganisations();
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
    }
  };

  // Decline invitation handler
  const handleDeclineInvitation = async (token) => {
    try {
      const { error } = await supabase
        .from('organisation_members')
        .update({
          invitation_status: 'declined'
        })
        .eq('invitation_token', token);

      if (error) throw error;

      toast.info('Invitation declined');
      fetchPendingInvitations();
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    }
  };

  // Permission helper — uses centralised RBAC
  const { can: canRBAC, canWithReason, isPro: rbacIsPro } = usePermissions();

  const canPerform = (permission) => {
    // Map legacy permission names to RBAC features
    const featureMap = {
      add_entries: FEATURES.EMISSIONS_ADD_ENTRY,
      export_reports: FEATURES.REPORTS_QUICK,
      access_strategy: FEATURES.AASB_S2_LAB,
      access_opportunities: FEATURES.GRANTS_SEARCH,
      manage_team: FEATURES.SETTINGS_TEAM_MGMT,
      manage_billing: FEATURES.SETTINGS_BILLING,
      view_dashboard: FEATURES.DASHBOARD_VIEW,
    };
    const feature = featureMap[permission];
    if (feature) return canRBAC(feature);
    // Fallback to old logic for unknown permissions
    if (!userRole) return true;
    if (userRole.is_superadmin) return true;
    return userRole.permissions?.[permission] ?? false;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clear grid region if state is not WA or NT
      const gridRegion = (profileData.state === 'WA' || profileData.state === 'NT')
        ? profileData.electricity_grid_region
        : null;

      // Update organisation directly in Supabase
      const { error } = await supabase
        .from('organisations')
        .update({
          name: profileData.company_name || 'My Organisation',
          abn: profileData.abn || null,
          state: profileData.state || null,
          electricity_grid_region: gridRegion,
          asrs_reporting_group: profileData.asrs_reporting_group ? parseInt(profileData.asrs_reporting_group) : null,
          sector: profileData.sector || null,
          employee_count: profileData.employee_count ? parseInt(profileData.employee_count) : null,
          reporting_year: profileData.reporting_year ? parseInt(profileData.reporting_year) : null,
          revenue: profileData.revenue ? parseFloat(profileData.revenue) : null,
          website: profileData.website || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      toast.success('Organisation profile updated!');
      setShowProfile(false);
      fetchOrganisations();
      fetchEmissionsWithDocs(); // Refetch emissions as CO2 values may have changed with state
      fetchGrantCompliance();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  // Redundant functions removed as they are now in hooks (useEmissionsData, useCamera, useEvidence)

  // Handle edit entry - populate form with existing data
  const handleEditEntry = (entry) => {
    // Open modal instead of scrolling to form
    const startDate = entry.start_date || entry.date || entry.entry_date;
    const endDate = entry.end_date;
    const isRange = !!endDate && startDate !== endDate;

    setEditModalData({
      category: entry.category,
      quantity: entry.quantity?.toString() || '',
      start_date: startDate ? new Date(startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      end_date: endDate ? new Date(endDate).toISOString().split('T')[0] : '',
      is_date_range: isRange,
      notes: entry.notes || ''
    });
    setShowEditModal(entry);
  };

  // Handle edit modal submission
  const handleEditModalSubmit = async () => {
    if (!showEditModal) return;

    if (editModalData.is_date_range && editModalData.end_date) {
      if (new Date(editModalData.end_date) < new Date(editModalData.start_date)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    setEditModalLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();

      const category = categories.find(c => c.value === editModalData.category);

      const startDate = editModalData.start_date || showEditModal.start_date || showEditModal.entry_date;
      const endDate = editModalData.is_date_range ? (editModalData.end_date || startDate) : startDate;
      const primaryFY = getFinancialYear(new Date(startDate));

      // Determine the state/grid region for electricity calculations
      // Use specific grid region for WA/NT if available
      const stateOrGrid = currentOrg?.electricity_grid_region || currentOrg?.state || 'NSW';

      // Use multi-year calculation for accurate FY-specific factors
      const multiYearResult = calculateMultiYearEmissions(
        editModalData.category,
        parseFloat(editModalData.quantity),
        stateOrGrid,
        startDate,
        endDate
      );

      // Create calcResult object for compatibility
      const calcResult = {
        co2_equivalent: multiYearResult.total_co2e_t,
        factor_value: multiYearResult.allocations.length > 0 ? multiYearResult.allocations[0].factor : null,
        factor_source: multiYearResult.allocations.length > 0
          ? multiYearResult.allocations.map(a => a.factor_source).join(', ')
          : 'NGA Factors'
      };

      // Update the entry
      const { error: updateError } = await supabase
        .from('emissions_data')
        .update({
          category: editModalData.category,
          quantity: parseFloat(editModalData.quantity),
          unit: category?.unit || '',
          start_date: startDate,
          end_date: endDate,
          total_tco2e: calcResult.co2_equivalent || 0,
          co2_equivalent: calcResult.co2_equivalent || 0,
          notes: editModalData.notes,
          factor_value_used: calcResult.factor_value || null,
          factor_source: calcResult.factor_source || null,
          financial_year: primaryFY
        })
        .eq('id', showEditModal.id);

      if (updateError) throw updateError;

      // Log to entry history
      const { error: historyError } = await supabase.from('entry_history').insert({
        id: crypto.randomUUID(),
        emission_entry_id: showEditModal.id,
        organisation_id: currentOrg.id,
        user_id: userData?.id,
        action: 'updated',
        category: editModalData.category,
        quantity: parseFloat(editModalData.quantity),
        unit: category?.unit || '',
        co2_equivalent: calcResult?.co2_equivalent || 0,
        entry_date: startDate,
        notes: editModalData.notes,
        user_email: authUser?.email,
        changes_json: JSON.stringify({
          previous: {
            category: showEditModal.category,
            quantity: showEditModal.quantity,
            notes: showEditModal.notes
          },
          new: {
            category: editModalData.category,
            quantity: parseFloat(editModalData.quantity),
            notes: editModalData.notes
          }
        })
      });

      if (historyError) console.error('History insert error:', historyError);

      toast.success('Entry updated successfully!');
      setShowEditModal(null);
      fetchEmissionsWithDocs();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error(error.message || 'Failed to update entry');
    } finally {
      setEditModalLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.quantity || !formData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.is_date_range && !formData.end_date) {
      toast.error('Please provide an end date for the selected range');
      return;
    }

    const categoryInfo = categories?.find(c => c.value === formData.category);
    if (!categoryInfo) {
      toast.error('Invalid category selected');
      return;
    }

    // Check subscription limits and role permissions early
    const currentEntryLimit = getEntryLimit(subscriptionStatus?.tier || 'demo');
    const subscriptionAllowsEntries = (currentOrg?.entry_count || 0) < currentEntryLimit;
    const roleAllowsEntries = canPerform('add_entries');
    const canAddEntries = subscriptionAllowsEntries && roleAllowsEntries;

    if (!canAddEntries) {
      setShowUpgradeModal(true);
      setShowForm(false);
      return;
    }
    try {
      if (editingEntry) {
        // Update existing entry using the hook
        await updateEmissionEntry(
          editingEntry.id,
          {
            category: formData.category,
            quantity: formData.quantity,
            entry_date: formData.start_date, // Using start_date to maintain compat
            notes: formData.notes
          },
          categoryInfo,
          editingEntry,
          async () => {
            setShowForm(false);
            setEditingEntry(null);
            setFormData({ category: '', quantity: '', start_date: new Date().toISOString().split('T')[0], end_date: '', is_date_range: false, entry_date: new Date().toISOString().split('T')[0], receipt_file: null, notes: '' });

            // Small delay to ensure database commit before refetching
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchEmissionsWithDocs();
            fetchOrganisations(); // Refresh entry count
          }
        );
      } else {
        // Add new entry using the hook
        await addEmissionEntry(formData, categoryInfo, async () => {
          setShowForm(false);
          setFormData({ category: '', quantity: '', start_date: new Date().toISOString().split('T')[0], end_date: '', is_date_range: false, entry_date: new Date().toISOString().split('T')[0], receipt_file: null, notes: '' });
          setFormCapturedImage(null);

          if (formReceiptInputRef.current) {
            formReceiptInputRef.current.value = '';
          }

          // Small delay to ensure database commit before refetching
          await new Promise(resolve => setTimeout(resolve, 500));
          await fetchEmissionsWithDocs();
          fetchOrganisations(); // Refresh entry count
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      // Toast error is handled by the hook
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleDeleteOrganisation = async () => {
    try {
      // Delete organisation directly in Supabase (cascade will handle related records)
      const { error } = await supabase
        .from('organisations')
        .delete()
        .eq('id', currentOrg.id);

      if (error) throw error;

      toast.success('Organisation deleted successfully');
      setShowProfile(false);
      setCurrentOrg(null);
      // Refresh organisations list
      fetchOrganisations();
    } catch (error) {
      console.error('Error deleting organisation:', error);
      toast.error(error.message || 'Failed to delete organisation');
    }
  };

  const currentEntryLimit = getEntryLimit(subscriptionStatus?.tier || 'demo');
  const subscriptionAllowsEntries = (currentOrg?.entry_count || 0) < currentEntryLimit;
  const roleAllowsEntries = canRBAC(FEATURES.EMISSIONS_ADD_ENTRY);
  const canAddEntries = subscriptionAllowsEntries && roleAllowsEntries;
  const entriesRemaining = currentEntryLimit - (currentOrg?.entry_count || 0);
  // Use RBAC-derived isPro
  const isPro = rbacIsPro;

  // Calculate setup progress percentage
  const calculateSetupProgress = () => {
    let progress = 0;
    if (profileData.company_name) progress += 25;
    if (profileData.state) progress += 25;
    if (profileData.sector) progress += 25;
    if (emissions.length > 0) progress += 25;
    return progress;
  };

  // Helper to get current period label for display
  const getPeriodLabel = () => {
    if (periodType === 'all_time') {
      return 'All-Time';
    } else if (periodType === 'custom') {
      const start = new Date(customDateRange.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      const end = new Date(customDateRange.endDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      return `${start} - ${end}`;
    } else {
      return `FY ${selectedYear - 1}/${selectedYear}`;
    }
  };

  // Helper to get short period label for dropdown button
  const getShortPeriodLabel = () => {
    if (periodType === 'all_time') {
      return 'All-Time';
    } else if (periodType === 'custom') {
      return 'Custom Period';
    } else {
      return `FY ${selectedYear - 1}/${selectedYear}`;
    }
  };

  // Helper function to calculate pro-rata allocation for a custom date range
  const calculateCustomPeriodAllocation = (entryStart, entryEnd, totalEmission, periodStart, periodEnd) => {
    const entryStartDate = new Date(entryStart);
    const entryEndDate = new Date(entryEnd);
    const rangeStart = new Date(periodStart);
    const rangeEnd = new Date(periodEnd);
    rangeEnd.setHours(23, 59, 59, 999);

    // Calculate total days of the entry
    const totalDays = Math.max(1, Math.ceil((entryEndDate - entryStartDate) / (1000 * 60 * 60 * 24)) + 1);

    // Calculate overlap with the custom range
    const overlapStart = new Date(Math.max(entryStartDate, rangeStart));
    const overlapEnd = new Date(Math.min(entryEndDate, rangeEnd));

    if (overlapStart > overlapEnd) {
      return { amount: 0, days: 0, isProrata: false };
    }

    const overlapDays = Math.max(1, Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1);
    const proRataAmount = (totalEmission / totalDays) * overlapDays;

    return {
      amount: proRataAmount,
      days: overlapDays,
      isProrata: overlapDays < totalDays
    };
  };

  // Filter emissions based on selected period type - WITH PRO-RATA ALLOCATION for FY and Custom
  const getFilteredEmissions = () => {
    if (periodType === 'all_time') {
      // Return ALL emissions without filtering
      return emissions.map(e => ({
        ...e,
        is_prorata: false
      }));
    } else if (periodType === 'custom') {
      // Filter by custom date range WITH pro-rata allocation
      const proRataEmissions = [];

      emissions.forEach(e => {
        const startDate = e.start_date || e.entry_date;
        const endDate = e.end_date || e.start_date || e.entry_date;
        const totalEmission = e.co2_equivalent || 0;

        if (!totalEmission || !startDate) return;

        const allocation = calculateCustomPeriodAllocation(
          startDate,
          endDate,
          totalEmission,
          customDateRange.startDate,
          customDateRange.endDate
        );

        if (allocation.amount > 0) {
          proRataEmissions.push({
            ...e,
            co2_equivalent: allocation.amount,
            prorata_days: allocation.days,
            original_co2: totalEmission,
            is_prorata: allocation.isProrata
          });
        }
      });

      return proRataEmissions;
    } else {
      // Financial Year - use pro-rata allocation
      const proRataEmissions = [];

      emissions.forEach(e => {
        // Use start_date and end_date if available, fallback to entry_date
        const startDate = e.start_date || e.entry_date;
        const endDate = e.end_date || e.start_date || e.entry_date;
        const totalEmission = e.co2_equivalent || 0;

        if (!totalEmission) return;

        // Calculate pro-rata allocation across FYs
        const allocations = calculateFYAllocation(startDate, endDate, totalEmission);

        // Find the allocation for the selected FY
        const fyAllocation = allocations.find(a => a.fyYear === selectedYear);

        if (fyAllocation && fyAllocation.amount > 0) {
          proRataEmissions.push({
            ...e,
            co2_equivalent: fyAllocation.amount,
            prorata_days: fyAllocation.days,
            original_co2: totalEmission,
            is_prorata: allocations.length > 1
          });
        }
      });

      return proRataEmissions;
    }
  };

  // Get filtered emissions based on current period selection
  const filteredEmissions = getFilteredEmissions();

  // Recalculate carbon summary client-side when period type changes to All-Time or Custom
  // This is needed because the server-side RPC only supports FY-based queries
  useEffect(() => {
    if (!currentOrg?.id || !emissions.length) return;

    if (periodType === 'financial_year') {
      // For FY, use the server-side calculated values
      fetchCarbonSummary();
    } else {
      // For All-Time or Custom, calculate from filteredEmissions client-side
      const grossEmissions = filteredEmissions.reduce((sum, e) => sum + (e.co2_equivalent || 0), 0);

      // For offsets, we need to calculate from allOffsetAllocations based on period
      // Offset allocations are tied to financial years, so we need to map dates to FYs
      let carbonOffsets = 0;

      if (periodType === 'all_time') {
        // Sum ALL offset allocations
        carbonOffsets = allOffsetAllocations.reduce((sum, alloc) => sum + (alloc.amount_tco2e || 0), 0);
      } else if (periodType === 'custom') {
        // For custom period, we need to determine which FYs fall within the date range
        // and sum allocations for those FYs (with potential pro-rata)
        const rangeStart = new Date(customDateRange.startDate);
        const rangeEnd = new Date(customDateRange.endDate);

        // Helper to get FY from a date (Australian FY: July 1 - June 30)
        const getFYFromDate = (date) => {
          const d = new Date(date);
          return d.getMonth() >= 6 ? d.getFullYear() + 1 : d.getFullYear();
        };

        const startFY = getFYFromDate(rangeStart);
        const endFY = getFYFromDate(rangeEnd);

        // Sum offsets for FYs that fall within or overlap the custom range
        carbonOffsets = allOffsetAllocations
          .filter(alloc => {
            const allocFY = alloc.financial_year;
            return allocFY >= startFY && allocFY <= endFY;
          })
          .reduce((sum, alloc) => sum + (alloc.amount_tco2e || 0), 0);
      }

      setCarbonSummary({
        gross_emissions: grossEmissions,
        carbon_offsets: carbonOffsets,
        net_emissions: grossEmissions - carbonOffsets,
        offsets_available: carbonOffsets > 0
      });
    }
  }, [periodType, customDateRange.startDate, customDateRange.endDate, emissions.length, currentOrg?.id, allOffsetAllocations]);

  // Recent entries sorted by Date Added (entry_date) - NOT filtered by period
  // This shows the 10 most recently entered records for audit trail purposes
  const recentEntriesByDateAdded = [...emissions].sort((a, b) => {
    const dateA = new Date(a.entry_date || a.created_at);
    const dateB = new Date(b.entry_date || b.created_at);
    return dateB - dateA; // Most recent first
  });

  // Total entries count including emissions + offset purchases + offset allocations
  // This matches what the /entries page shows
  const totalEntriesCount = emissions.length + recentActivity.length;

  // Calculate scope breakdowns (Scope 1 & 2 only)
  const calculateScopeData = () => {
    const scopes = { 'Scope 1': 0, 'Scope 2': 0 };

    filteredEmissions.forEach(e => {
      const cat = categories.find(c => c.value === e.category);
      if (cat && e.co2_equivalent && (cat.scope === 'Scope 1' || cat.scope === 'Scope 2')) {
        scopes[cat.scope] += e.co2_equivalent;
      }
    });

    return Object.entries(scopes)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({
        name,
        value: parseFloat(value.toFixed(2)),
        color: name === 'Scope 1' ? '#E76F51' : '#E9C46A'
      }));
  };

  // Compliance checklist (Scope 1 & 2 categories)
  const getComplianceStatus = () => {
    const categoriesTracked = new Set(filteredEmissions.map(e => e.category));
    return [
      { item: 'Electricity Data Logged', completed: categoriesTracked.has('electricity') || categoriesTracked.has('electricity_greenpower') },
      { item: 'Fuel/Transport Data Logged', completed: categoriesTracked.has('fuel_petrol') || categoriesTracked.has('fuel_diesel') || categoriesTracked.has('fuel_lpg') },
      { item: 'Natural Gas Data Logged', completed: categoriesTracked.has('natural_gas') },
    ];
  };

  const getCategoryInfo = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || {};
  };

  // Client-side CSV export function
  const exportToCSV = async (forYear = null) => {
    try {
      // Fetch all data for export
      let query = supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('entry_date', { ascending: false });

      if (forYear && forYear > 0) {
        query = query.eq('financial_year', forYear);
      }

      const { data: exportData, error } = await query;
      if (error) throw error;

      if (!exportData || exportData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Scope mapping (Scope 1 & 2 only)
      const scopeMapping = {
        'fuel_petrol': 'Scope 1', 'fuel_diesel': 'Scope 1', 'fuel_lpg': 'Scope 1', 'natural_gas': 'Scope 1',
        'electricity': 'Scope 2', 'electricity_greenpower': 'Scope 2'
      };

      // Transform data for CSV
      const csvData = exportData.map(e => ({
        'Entry Date': e.entry_date ? new Date(e.entry_date).toLocaleDateString('en-AU') : '',
        'Category': getCategoryInfo(e.category).label || e.category,
        'Scope': scopeMapping[e.category] || 'Unknown',
        'Quantity': e.quantity,
        'Unit': e.unit,
        'CO2 Equivalent (tonnes)': e.co2_equivalent ? e.co2_equivalent.toFixed(6) : '',
        'Financial Year': e.financial_year,
        'Notes': e.notes || '',
        'Created At': e.created_at ? new Date(e.created_at).toLocaleString('en-AU') : ''
      }));

      // Generate CSV
      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const filename = forYear && forYear > 0
        ? `emissions_FY${forYear}.csv`
        : `emissions_all_years.csv`;
      saveAs(blob, filename);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.message || 'Unknown error'));
    }
  };

  // Client-side XLS export function (as CSV with .xls extension for basic Excel compatibility)
  const exportToXLS = async (forYear = null) => {
    try {
      // Fetch all data for export
      let query = supabase
        .from('emissions_data')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('entry_date', { ascending: false });

      if (forYear && forYear > 0) {
        query = query.eq('financial_year', forYear);
      }

      const { data: exportData, error } = await query;
      if (error) throw error;

      if (!exportData || exportData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Scope mapping (Scope 1 & 2 only)
      const scopeMapping = {
        'fuel_petrol': 'Scope 1', 'fuel_diesel': 'Scope 1', 'fuel_lpg': 'Scope 1', 'natural_gas': 'Scope 1',
        'electricity': 'Scope 2', 'electricity_greenpower': 'Scope 2'
      };

      // Create HTML table for Excel
      let html = '<html><head><meta charset="UTF-8"></head><body>';
      html += '<table border="1">';
      html += '<tr><th>Entry Date</th><th>Category</th><th>Scope</th><th>Quantity</th><th>Unit</th><th>CO2 Equivalent (tonnes)</th><th>Financial Year</th><th>Notes</th><th>Created At</th></tr>';

      exportData.forEach(e => {
        html += '<tr>';
        html += `<td>${e.entry_date ? new Date(e.entry_date).toLocaleDateString('en-AU') : ''}</td>`;
        html += `<td>${getCategoryInfo(e.category).label || e.category}</td>`;
        html += `<td>${scopeMapping[e.category] || 'Unknown'}</td>`;
        html += `<td>${e.quantity}</td>`;
        html += `<td>${e.unit}</td>`;
        html += `<td>${e.co2_equivalent ? e.co2_equivalent.toFixed(6) : ''}</td>`;
        html += `<td>${e.financial_year || ''}</td>`;
        html += `<td>${e.notes || ''}</td>`;
        html += `<td>${e.created_at ? new Date(e.created_at).toLocaleString('en-AU') : ''}</td>`;
        html += '</tr>';
      });

      html += '</table></body></html>';

      const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const filename = forYear && forYear > 0
        ? `emissions_FY${forYear}.xls`
        : `emissions_all_years.xls`;
      saveAs(blob, filename);
      toast.success('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.message || 'Unknown error'));
    }
  };

  const totalEmissions = filteredEmissions.reduce((sum, e) => sum + (e.co2_equivalent || 0), 0);
  const scopeData = calculateScopeData();

  // Calculate category data for second pie chart
  const calculateCategoryData = () => {
    const categoryTotals = {};
    filteredEmissions.forEach(e => {
      const cat = categories.find(c => c.value === e.category);
      if (cat && e.co2_equivalent) {
        const label = cat.label || e.category;
        categoryTotals[label] = (categoryTotals[label] || 0) + e.co2_equivalent;
      }
    });

    // Convert to array and sort by value descending
    return Object.entries(categoryTotals)
      .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  };

  const categoryData = calculateCategoryData();

  // Toggle flip card state
  const toggleFlipCard = (cardKey) => {
    setFlippedCards(prev => ({
      ...prev,
      [cardKey]: !prev[cardKey]
    }));
  };

  // Professional color palette for categories
  const categoryColors = [
    '#3B82F6', // Blue
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16', // Lime
    '#F97316', // Orange
    '#6366F1', // Indigo
  ];

  const complianceStatus = getComplianceStatus();
  const compliancePercentage = Math.round((complianceStatus.filter(s => s.completed).length / complianceStatus.length) * 100);

  // Get available financial years
  const availableYears = [...new Set(emissions.map(e => e.financial_year || new Date().getFullYear()))].sort((a, b) => b - a);

  // Show loading spinner while initial data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003B5C] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard-page">
      {/* DEPRECATED: Header & Modals section removed - now handled by Sub-components or MemberLayout */}

      {/* Pending Invitations Banner */}
      {pendingInvitations.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">You have pending team invitations</h3>
                <div className="space-y-2 mt-2">
                  {pendingInvitations.map((inv) => (
                    <div key={inv.invitation_token} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                      <div>
                        <span className="font-medium text-slate-800">{inv.organisation_name}</span>
                        <span className="text-slate-500 text-sm ml-2">as {inv.role === 'full_access' ? 'Full Access' : inv.role === 'editor' ? 'Editor' : 'View Only'}</span>
                        <div className="text-xs text-slate-400">Invited by {inv.invited_by}</div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeclineInvitation(inv.invitation_token)}
                          className="text-slate-600"
                        >
                          Decline
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptInvitation(inv.invitation_token)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Accept
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role-based access warning for restricted users */}
      {userRole && !canPerform('add_entries') && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center space-x-3">
            <Eye className="w-5 h-5 text-slate-400" />
            <p className="text-sm text-slate-600">
              <span className="font-medium">View Only Access</span> — You can view the dashboard and export reports. Contact the owner for editing permissions.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title with Action Button pattern */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <BarChart3 className="w-7 h-7 mr-3 text-[#74A57F]" />
              Dashboard
            </h1>
            <p className="text-slate-600 mt-1">{currentOrg?.name} • Overview</p>
          </div>
        </div>

        {/* Reduction Target Progress Card (replaced carousel) */}
        {currentOrg && (
          <div className="mb-6">
            <ReductionTargetCard
              reductionTarget={reductionTarget}
              onSetTarget={() => setShowTargetModal(true)}
              onConfigure={() => setShowTargetModal(true)}
            />
          </div>
        )}

        {/* Setup Progress Tracker */}
        {currentOrg && !currentOrg.setup_complete && (
          <div className="bg-gradient-to-r from-[#003B5C] to-[#005580] rounded-2xl p-6 mb-6 text-white" data-testid="setup-progress">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Complete Your Setup</h3>
                  <p className="text-white/80 text-sm">
                    Finish setting up your organisation profile to get accurate emissions calculations
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right hidden md:block">
                  <div className="text-sm text-white/60">Progress</div>
                  <div className="font-semibold">
                    {calculateSetupProgress()}% complete
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/setup')}
                  className="bg-white text-[#003B5C] hover:bg-white/90"
                  data-testid="complete-setup-btn"
                >
                  Complete Setup
                </Button>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 bg-white/20 rounded-full h-2">
              <div
                className="bg-[#74A57F] h-2 rounded-full transition-all duration-500"
                style={{ width: `${calculateSetupProgress()}%` }}
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span className={`flex items-center ${profileData.company_name ? 'text-[#74A57F]' : 'text-white/60'}`}>
                {profileData.company_name ? <Check className="w-4 h-4 mr-1" /> : <span className="w-4 h-4 mr-1 rounded-full border border-white/40 inline-block" />}
                Organisation name
              </span>
              <span className={`flex items-center ${profileData.state ? 'text-[#74A57F]' : 'text-white/60'}`}>
                {profileData.state ? <Check className="w-4 h-4 mr-1" /> : <span className="w-4 h-4 mr-1 rounded-full border border-white/40 inline-block" />}
                State/Territory
              </span>
              <span className={`flex items-center ${profileData.sector ? 'text-[#74A57F]' : 'text-white/60'}`}>
                {profileData.sector ? <Check className="w-4 h-4 mr-1" /> : <span className="w-4 h-4 mr-1 rounded-full border border-white/40 inline-block" />}
                Industry sector
              </span>
              <span className={`flex items-center ${emissions.length > 0 ? 'text-[#74A57F]' : 'text-white/60'}`}>
                {emissions.length > 0 ? <Check className="w-4 h-4 mr-1" /> : <span className="w-4 h-4 mr-1 rounded-full border border-white/40 inline-block" />}
                First data entry
              </span>
            </div>
          </div>
        )}

        {/* Usage Meter for Free Trial */}
        {!isPro && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="usage-meter">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#003B5C]">Trial Progress</h3>
                  <p className="text-sm text-slate-600">
                    <span className="font-bold text-amber-600">{currentOrg?.entry_count || 0}</span> of <span className="font-bold">{formatLimit(currentEntryLimit)}</span> entries used
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-48 bg-amber-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(((currentOrg?.entry_count || 0) / currentEntryLimit) * 100, 100)}%` }}
                  />
                </div>
                <Button
                  onClick={() => setShowUpgradeModal(true)}
                  size="sm"
                  className="bg-[#003B5C] text-white hover:bg-[#002A42]"
                  data-testid="upgrade-from-meter"
                >
                  <Crown className="w-4 h-4 mr-1" />
                  Upgrade
                </Button>
              </div>
            </div>
            {entriesRemaining <= 2 && entriesRemaining > 0 && (
              <p className="text-xs text-amber-700 mt-2 ml-14">
                Only {entriesRemaining} {entriesRemaining === 1 ? 'entry' : 'entries'} remaining! Upgrade to continue tracking your emissions.
              </p>
            )}
            {entriesRemaining === 0 && (
              <p className="text-xs text-red-600 mt-2 ml-14 font-medium">
                Entry limit reached! Upgrade to Pro to continue adding data.
              </p>
            )}
          </div>
        )}

        {/* Carbon Footprint Summary - Three Key Metrics */}
        <div className="mb-6">
          <DashboardSummary
            carbonSummary={carbonSummary}
            isPro={subscriptionStatus?.tier === 'pro'}
            flippedCards={flippedCards}
            toggleFlipCard={toggleFlipCard}
            getPeriodLabel={getPeriodLabel}
            setShowUpgradeModal={setShowUpgradeModal}
            complianceStatus={getComplianceStatus()}
            pdfDownloading={pdfDownloading}
            emissionsLength={emissions.length}
            setShowReportConsentModal={setShowReportConsentModal}
          />
        </div>

        <div className="mb-6">
          <DashboardCharts
            scopeData={scopeData}
            categoryData={categoryData}
            getShortPeriodLabel={getShortPeriodLabel}
            categoryColors={categoryColors}
            filteredEmissions={filteredEmissions}
            categories={categories}
            compliancePercentage={compliancePercentage}
            helpContent={helpContent}
            emissions={emissions}
          />
        </div>

        {/* Data Entry Actions / Form */}
        {showForm || editingEntry ? (
          <div className="mb-6">
            <DataEntryForm
              canAddEntries={canAddEntries}
              entriesRemaining={entriesRemaining}
              FREE_TIER_ENTRY_LIMIT={currentEntryLimit}
              isPro={isPro}
              showForm={showForm}
              setShowForm={setShowForm}
              editingEntry={editingEntry}
              setEditingEntry={setEditingEntry}
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
              handleCategoryChange={handleCategoryChange}
              flatCategories={categories}
              groupedCategories={groupedCategories}
              categoriesLoading={categoriesLoading}
              helpContent={helpContent}
              getFinancialYear={getFinancialYear}
              getReportingPeriods={getReportingPeriods}
              getFYLabel={getFYLabel}
              showReceiptScanner={showReceiptScanner}
              setShowReceiptScanner={setShowReceiptScanner}
              setReceiptScanCategory={setReceiptScanCategory}
              navigate={navigate}
              setShowUpgradeModal={setShowUpgradeModal}
              formVideoRef={formVideoRef}
              formCanvasRef={formCanvasRef}
              formCameraMode={formCameraMode}
              formCapturedImage={formCapturedImage}
              startFormCamera={startFormCamera}
              stopFormCamera={stopFormCamera}
              captureFormPhoto={captureFormImage}
              retakeFormPhoto={() => setFormCapturedImage(null)}
            />
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-slate-100 mb-6">
            <h2 className="text-xl font-semibold text-[#003B5C] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Data Entry for Emissions & Carbon Credits
            </h2>
            <div className="flex flex-wrap gap-4">
              {!canWithReason(FEATURES.EMISSIONS_OCR_IMPORT).isHidden && (
                <Button
                  onClick={() => setShowReceiptScanner(true)}
                  variant="outline"
                  className="border-[#003B5C] text-[#003B5C] hover:bg-slate-50 flex items-center h-10 px-4 rounded-lg font-medium transition-colors"
                  data-testid="scan-receipt-btn"
                >
                  <ScanLine className="w-4 h-4 mr-2" />
                  Scan Receipt
                </Button>
              )}
              {!canWithReason(FEATURES.EMISSIONS_BULK_CSV).isHidden && (
                <Button
                  onClick={() => setShowCsvImporter(true)}
                  variant="outline"
                  className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F]/10 flex items-center h-10 px-4 rounded-lg font-medium transition-colors"
                  data-testid="import-csv-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              )}
              {!canWithReason(FEATURES.EMISSIONS_ADD_ENTRY).isHidden && (
                <Button
                  onClick={() => setShowForm(true)}
                  variant="outline"
                  className="border-red-400 text-red-500 hover:bg-red-50 flex items-center h-10 px-4 rounded-lg font-medium transition-colors"
                  data-testid="add-emissions-btn"
                >
                  <Flame className="w-4 h-4 mr-2" />
                  Add Emissions Entry
                </Button>
              )}
              {isPro && !canWithReason(FEATURES.EMISSIONS_ADD_CARBON_CREDIT).isHidden && (
                <Button
                  onClick={() => navigate('/offsets')}
                  variant="outline"
                  className="border-[#74A57F] text-[#74A57F] hover:bg-[#74A57F]/10 flex items-center h-10 px-4 rounded-lg font-medium transition-colors"
                  data-testid="add-carbon-credit-btn"
                >
                  <Leaf className="w-4 h-4 mr-2" />
                  Add Carbon Credit
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Recent Data Entries */}
        <EmissionsTable
          loading={loading}
          emissions={emissions}
          recentActivity={recentActivity}
          totalEntriesCount={totalEntriesCount}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          openActionMenu={openActionMenu}
          setOpenActionMenu={setOpenActionMenu}
          showDeleteConfirm={showDeleteConfirm}
          setShowDeleteConfirm={setShowDeleteConfirm}
          handleEditEntry={handleEditEntry}
          handleDeleteEntry={handleDeleteEntry}
          fetchEntryHistory={async (id) => {
            setShowHistoryModal(id);
            await fetchEntryHistory(id);
          }}
          setShowEvidenceUpload={setShowEvidenceUpload}
          showEvidenceDeleteConfirm={showEvidenceDeleteConfirm}
          setShowEvidenceDeleteConfirm={setShowEvidenceDeleteConfirm}
          handleDeleteEvidence={handleDeleteEvidence}
          deletingEvidence={deletingEvidence}
          deletingEntry={deletingEntry}
          navigate={navigate}
          subscriptionStatus={subscriptionStatus}
          recentEntriesByDateAdded={recentEntriesByDateAdded}
          getFYLabel={getFYLabel}
          getReportingPeriods={getReportingPeriods}
          selectedYear={selectedYear}
          formatActivityDate={formatActivityDate}
          formatEntryDate={formatEntryDate}
          currentOrg={currentOrg}
          getCategoryInfo={getCategoryInfo}
          exportToCSV={exportToCSV}
          exportToXLS={exportToCSV}
          setShowExportMenu={setShowExportMenu}
          showExportMenu={showExportMenu}
          getShortPeriodLabel={getShortPeriodLabel}
          supabase={supabase}
          setShowForm={setShowForm}
        />
      </div>

      <EvidenceModal
        showEvidenceUpload={showEvidenceUpload}
        closeEvidenceModal={closeEvidenceModal}
        handleEvidenceUpload={handleEvidenceUpload}
        evidenceInputRef={evidenceInputRef}
        canvasRef={canvasRef}
        cameraMode={cameraMode}
        capturedImage={capturedImage}
        videoRef={videoRef}
        startCamera={startEvidenceCamera}
        stopCamera={stopEvidenceCamera}
        capturePhoto={captureEvidenceImage}
        retakePhoto={retakePhoto}
        uploadCapturedImage={uploadCapturedImage}
        evidenceUploading={evidenceUploading}
      />


      {/* Data History Modal */}
      {
        showHistoryModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="history-modal">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Data History
                  </h2>
                  <p className="text-sm text-slate-500">Audit trail for this entry</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowHistoryModal(null);
                  }}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#003B5C]" />
                    <span className="ml-2 text-slate-600">Loading history...</span>
                  </div>
                ) : entryHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">No history records found for this entry.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {entryHistory.map((record, index) => {
                      const isLatest = index === 0;
                      return (
                        <div
                          key={record.id}
                          className={`relative pl-6 pb-4 ${index < entryHistory.length - 1 ? 'border-l-2 border-slate-200' : ''}`}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-[-8px] top-0 w-4 h-4 rounded-full border-2 ${record.action === 'created' ? 'bg-green-500 border-green-500' :
                            record.action === 'updated' ? 'bg-blue-500 border-blue-500' :
                              record.action === 'deleted' ? 'bg-red-500 border-red-500' :
                                'bg-slate-400 border-slate-400'
                            }`} />

                          <div className="bg-slate-50 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${record.action === 'created' ? 'bg-green-100 text-green-800' :
                                  record.action === 'updated' ? 'bg-blue-100 text-blue-800' :
                                    record.action === 'deleted' ? 'bg-red-100 text-red-800' :
                                      record.action === 'evidence_added' ? 'bg-purple-100 text-purple-800' :
                                        'bg-slate-100 text-slate-800'
                                  }`}>
                                  {record.action === 'created' && 'Created'}
                                  {record.action === 'updated' && 'Updated'}
                                  {record.action === 'deleted' && 'Deleted'}
                                  {record.action === 'evidence_added' && 'Evidence Added'}
                                  {record.action === 'evidence_removed' && 'Evidence Removed'}
                                </span>
                                {isLatest && (
                                  <span className="ml-2 text-xs text-slate-400">Current</span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {new Date(record.created_at).toLocaleString()}
                              </div>
                            </div>

                            <div className="text-sm space-y-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-slate-600">Category:</span>
                                <span className="font-medium text-[#003B5C]">
                                  {categories.find(c => c.value === record.category)?.label || record.category}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-slate-600">Quantity:</span>
                                <span className="font-medium text-[#003B5C]">
                                  {record.quantity} {record.unit}
                                </span>
                              </div>
                              {record.co2_equivalent && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-slate-600">CO₂-e:</span>
                                  <span className="font-medium text-[#74A57F]">
                                    {record.co2_equivalent.toFixed(4)} tonnes
                                  </span>
                                </div>
                              )}
                              {record.factor_year_used && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-slate-600">Factor Year:</span>
                                  <span className="text-slate-700">NGA {record.factor_year_used}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <span className="text-slate-600">By:</span>
                                <span className="text-slate-700">{record.user_email || 'Unknown'}</span>
                              </div>
                            </div>

                            {record.notes && (
                              <div className="mt-2 text-xs text-slate-500 italic">
                                Note: {record.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-100 bg-slate-50">
                <p className="text-xs text-slate-500 text-center">
                  Version history is automatically maintained for AASB S2 compliance and audit readiness.
                </p>
              </div>
            </div>
          </div>
        )
      }

      {/* Reduction Target Modal */}
      {
        showTargetModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-xl font-bold text-[#003B5C]">Set Reduction Target</h2>
                <p className="text-slate-500 text-sm mt-1">Define your emissions reduction goal</p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-700 flex items-center">
                    Baseline Year
                    <HelpTooltip {...helpContent.baselineYear} />
                  </Label>
                  <Select
                    value={targetFormData.baseline_year.toString()}
                    onValueChange={(val) => setTargetFormData({ ...targetFormData, baseline_year: parseInt(val) })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>FY {y - 1}/{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Your emissions in this year will be used as the baseline</p>
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center">
                    Reduction Target (%)
                    <HelpTooltip {...helpContent.reductionTarget} />
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={targetFormData.reduction_target_percent}
                    onChange={(e) => setTargetFormData({ ...targetFormData, reduction_target_percent: parseFloat(e.target.value) })}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">e.g., 20 for a 20% reduction target</p>
                </div>
                <div>
                  <Label className="text-slate-700">Target Year</Label>
                  <Select
                    value={targetFormData.target_year.toString()}
                    onValueChange={(val) => setTargetFormData({ ...targetFormData, target_year: parseInt(val) })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2028, 2029, 2030, 2035, 2040, 2050].map(y => (
                        <SelectItem key={y} value={y.toString()}>FY {y - 1}/{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end space-x-3">
                <Button onClick={() => setShowTargetModal(false)} variant="outline">Cancel</Button>
                <Button onClick={saveReductionTarget} className="bg-[#74A57F] hover:bg-[#5F8575] text-white">
                  Save Target
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Demo Entries Modal - shown after upgrade from Demo tier */}
      {
        showDemoEntriesModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#003B5C]">Welcome to Your Paid Plan!</h2>
                    <p className="text-slate-500 text-sm">You have demo entries to review</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <p className="text-slate-700">
                    You have <span className="font-bold text-[#003B5C]">{demoEntriesCount}</span> emission {demoEntriesCount === 1 ? 'entry' : 'entries'} from your demo period.
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    Would you like to keep these entries in your account, or start fresh with a clean slate?
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleDemoEntries('keep')}
                    disabled={handlingDemoEntries}
                    className="w-full p-4 border-2 border-[#74A57F] rounded-xl text-left hover:bg-[#74A57F]/5 transition-colors disabled:opacity-50"
                    data-testid="keep-demo-entries-btn"
                  >
                    <div className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-[#74A57F]" />
                      <div>
                        <div className="font-semibold text-[#003B5C]">Keep My Entries</div>
                        <div className="text-sm text-slate-500">Convert demo entries to regular entries</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleDemoEntries('delete')}
                    disabled={handlingDemoEntries}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl text-left hover:bg-slate-50 transition-colors disabled:opacity-50"
                    data-testid="delete-demo-entries-btn"
                  >
                    <div className="flex items-center space-x-3">
                      <Trash2 className="w-5 h-5 text-slate-400" />
                      <div>
                        <div className="font-semibold text-slate-700">Start Fresh</div>
                        <div className="text-sm text-slate-500">Delete all demo entries and begin anew</div>
                      </div>
                    </div>
                  </button>
                </div>

                {handlingDemoEntries && (
                  <div className="mt-4 flex items-center justify-center space-x-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Entry Modal */}
      {
        showEditModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Edit Entry
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowEditModal(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <Label className="text-slate-700">Category</Label>
                  <Select
                    value={editModalData.category}
                    onValueChange={(val) => setEditModalData({ ...editModalData, category: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {Object.entries(groupedCategories).map(([group, items]) => (
                        <div key={group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">{group}</div>
                          {items.map((cat) => {
                            const Icon = cat.icon;
                            return (
                              <SelectItem key={cat.value} value={cat.value} className="py-2.5 px-3 focus:bg-slate-50">
                                <div className="flex flex-col w-full text-left">
                                  <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                      {Icon && <Icon className="w-4 h-4" style={{ color: cat.color }} />}
                                      <span className="font-semibold text-slate-800 text-sm">
                                        {cat.label}
                                        <span className="text-slate-400 font-normal ml-1">({cat.unit})</span>
                                      </span>
                                    </div>
                                    {cat.zeroEmission && (
                                      <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
                                        Zero
                                      </span>
                                    )}
                                  </div>
                                  {(cat.rawRegion || cat.rawNote) && (
                                    <div className="text-[10px] text-slate-500 mt-1 ml-6 leading-tight">
                                      <span className="font-medium text-slate-400 capitalize">{cat.rawRegion}</span>
                                      {cat.rawNote && (
                                        <span className="before:content-['•'] before:mx-1.5 before:text-slate-300">
                                          {cat.rawNote}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-700">
                    Quantity {categories.find(c => c.value === editModalData.category)?.unit && (
                      <span className="text-slate-500 ml-1">({categories.find(c => c.value === editModalData.category)?.unit})</span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editModalData.quantity}
                    onChange={(e) => setEditModalData({ ...editModalData, quantity: e.target.value })}
                    className="mt-1"
                    placeholder="Enter quantity"
                  />
                </div>

                {/* Date Type Toggle */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {editModalData.is_date_range ? (
                        <CalendarRange className="w-4 h-4 text-[#003B5C]" />
                      ) : (
                        <CalendarIcon className="w-4 h-4 text-[#003B5C]" />
                      )}
                      <Label className="text-slate-700 font-medium">
                        {editModalData.is_date_range ? 'Date Range' : 'Single Date'}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-slate-500">Point-in-time</span>
                      <Switch
                        checked={editModalData.is_date_range}
                        onCheckedChange={(checked) => setEditModalData({
                          ...editModalData,
                          is_date_range: checked,
                          end_date: checked ? editModalData.end_date || editModalData.start_date : ''
                        })}
                      />
                      <span className="text-xs text-slate-500">Period</span>
                    </div>
                  </div>
                </div>

                {/* Date Fields */}
                <div className={editModalData.is_date_range ? 'grid grid-cols-2 gap-3' : ''}>
                  <div>
                    <Label className="text-slate-700">
                      {editModalData.is_date_range ? 'Start Date' : 'Activity Date'}
                    </Label>
                    <Input
                      type="date"
                      value={editModalData.start_date}
                      onChange={(e) => setEditModalData({ ...editModalData, start_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  {editModalData.is_date_range && (
                    <div>
                      <Label className="text-slate-700">End Date</Label>
                      <Input
                        type="date"
                        value={editModalData.end_date}
                        onChange={(e) => setEditModalData({ ...editModalData, end_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-700">Reference Notes (optional)</Label>
                  <Input
                    type="text"
                    value={editModalData.notes}
                    onChange={(e) => setEditModalData({ ...editModalData, notes: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., Invoice #12345, Bill reference"
                  />
                </div>

                {/* Evidence Actions */}
                <div className="pt-4 border-t border-slate-100">
                  <Label className="text-slate-700 mb-2 block">Evidence</Label>
                  {showEditModal.documents?.length > 0 ? (
                    <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <Paperclip className="w-4 h-4 text-[#74A57F]" />
                        <span className="text-sm text-slate-600">Evidence attached</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const storagePath = showEditModal.documents?.[0]?.storage_path;
                            if (storagePath) {
                              const { data, error } = await supabase.storage
                                .from('evidence-vault')
                                .createSignedUrl(storagePath, 3600);
                              if (!error && data?.signedUrl) {
                                window.open(data.signedUrl, '_blank');
                              } else {
                                toast.error('Could not access file');
                              }
                            }
                          }}
                          className="text-[#003B5C]"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleDeleteEvidence(showEditModal);
                            setShowEditModal(null);
                          }}
                          className="text-red-500 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEvidenceUpload(showEditModal.id);
                        setShowEditModal(null);
                      }}
                      className="w-full text-[#74A57F] border-[#74A57F] hover:bg-[#74A57F] hover:text-white"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Attach Evidence
                    </Button>
                  )}
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(showEditModal.id);
                    setShowEditModal(null);
                  }}
                  className="text-red-500 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Entry
                </Button>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setShowEditModal(null)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEditModalSubmit}
                    disabled={editModalLoading}
                    className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                  >
                    {editModalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Custom Date Range Picker Modal */}
      {
        showCustomDatePicker && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="custom-date-picker-modal">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Select Custom Date Range
                </h2>
                <button
                  onClick={() => {
                    setShowCustomDatePicker(false);
                    setTempDateRange({ start: null, end: null });
                    // If no valid custom range was set, revert to financial year
                    if (!customDateRange.startDate || !customDateRange.endDate) {
                      setPeriodType('financial_year');
                    }
                  }}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Start Date */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Start Date</Label>
                  <div className="border rounded-lg p-2 bg-slate-50">
                    <Calendar
                      mode="single"
                      selected={tempDateRange.start || (customDateRange.startDate ? new Date(customDateRange.startDate) : undefined)}
                      onSelect={(date) => setTempDateRange(prev => ({ ...prev, start: date }))}
                      disabled={(date) => tempDateRange.end ? date > tempDateRange.end : false}
                      initialFocus
                      className="mx-auto"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Selected: {tempDateRange.start ? tempDateRange.start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : (customDateRange.startDate ? new Date(customDateRange.startDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not selected')}
                  </p>
                </div>

                {/* End Date */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">End Date</Label>
                  <div className="border rounded-lg p-2 bg-slate-50">
                    <Calendar
                      mode="single"
                      selected={tempDateRange.end || (customDateRange.endDate ? new Date(customDateRange.endDate) : undefined)}
                      onSelect={(date) => setTempDateRange(prev => ({ ...prev, end: date }))}
                      disabled={(date) => tempDateRange.start ? date < tempDateRange.start : false}
                      className="mx-auto"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Selected: {tempDateRange.end ? tempDateRange.end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : (customDateRange.endDate ? new Date(customDateRange.endDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Not selected')}
                  </p>
                </div>
              </div>

              {/* Quick Select Options */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Quick Select</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), today.getMonth(), 1);
                      setTempDateRange({ start, end: today });
                    }}
                    className="text-xs"
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
                      setTempDateRange({ start, end: today });
                    }}
                    className="text-xs"
                  >
                    Last 3 Months
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
                      setTempDateRange({ start, end: today });
                    }}
                    className="text-xs"
                  >
                    Last 6 Months
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
                      setTempDateRange({ start, end: today });
                    }}
                    className="text-xs"
                  >
                    Last 12 Months
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const start = new Date(today.getFullYear(), 0, 1);
                      setTempDateRange({ start, end: today });
                    }}
                    className="text-xs"
                  >
                    This Calendar Year
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const lastYear = new Date().getFullYear() - 1;
                      const start = new Date(lastYear, 0, 1);
                      const end = new Date(lastYear, 11, 31);
                      setTempDateRange({ start, end });
                    }}
                    className="text-xs"
                  >
                    Last Calendar Year
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCustomDatePicker(false);
                    setTempDateRange({ start: null, end: null });
                    // If no valid custom range, revert
                    if (!customDateRange.startDate || !customDateRange.endDate) {
                      setPeriodType('financial_year');
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const startDate = tempDateRange.start || (customDateRange.startDate ? new Date(customDateRange.startDate) : null);
                    const endDate = tempDateRange.end || (customDateRange.endDate ? new Date(customDateRange.endDate) : null);

                    if (!startDate || !endDate) {
                      toast.error('Please select both start and end dates');
                      return;
                    }

                    if (startDate > endDate) {
                      toast.error('Start date must be before end date');
                      return;
                    }

                    setCustomDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setPeriodType('custom');
                    setShowCustomDatePicker(false);
                    setShowOrgSwitcher(false);
                    setTempDateRange({ start: null, end: null });
                    toast.success(`Viewing data from ${startDate.toLocaleDateString('en-AU')} to ${endDate.toLocaleDateString('en-AU')}`);
                  }}
                  className="bg-[#74A57F] hover:bg-[#5F8575] text-white"
                  data-testid="apply-date-range-btn"
                >
                  Apply Date Range
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {showCsvImporter && (
        <CsvImporter
          onClose={() => setShowCsvImporter(false)}
          onSuccess={() => window.location.reload()}
        />
      )}

      {showReceiptScanner && (
        <ReceiptScanner
          onClose={() => setShowReceiptScanner(false)}
          onDataExtracted={(data) => {
            setShowReceiptScanner(false);
            setFormData(prev => ({
              ...prev,
              category: data.category || 'electricity',
              quantity: data.quantity,
              start_date: data.start_date,
              end_date: data.end_date || data.start_date,
              is_date_range: !!data.end_date && data.end_date !== data.start_date,
              unit: data.unit
            }));
            setShowForm(true);
          }}
        />
      )}

      {/* DEPRECATED: AI Co-Pilot - now provided by MemberLayout */}
      {/* TODO: Remove this once MemberLayout is stable 
      <AICoPilot 
        organisationId={currentOrg?.id}
        organisationName={currentOrg?.name}
        subscriptionTier={subscriptionStatus?.tier || currentOrg?.subscription_tier || 'demo'}
        currentPage="dashboard"
      />
      */}
    </div>
  );
};

export default DashboardPage;
